import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  AgentContent,
  GeminiAgentHistoryService
} from './gemini-agent-history.service';

export interface AgentGenerationConfig {
  temperature?: number;
  topP?: number;
  topK?: number;
  maxOutputTokens?: number;
  [key: string]: unknown;
}

export interface AgentRequest {
  model: string;
  context?: string;
  config?: AgentGenerationConfig;
  history?: AgentContent[];
  prompt: string;
  instruction?: string;
  responseSchema?: unknown;
  tools?: unknown[];
}

export interface AgentResponse {
  content: unknown;
  raw?: unknown;
}

interface AgentOptions {
  model?: string;
  context?: string;
  instruction?: string;
  responseSchema?: unknown;
  config?: AgentGenerationConfig;
  history?: AgentContent[];
  tools?: unknown[];
}

@Injectable({
  providedIn: 'root',
})
export class GeminiAgentService {
  private readonly endpoint =
    'https://gemini-agent-o3e5hblbqa-uc.a.run.app/agent';
  private readonly defaultModel = 'gemini-flash-latest';

  constructor(
    private readonly http: HttpClient,
    private readonly historyService: GeminiAgentHistoryService
  ) {}

  async sendPrompt(
    prompt: string,
    options: AgentOptions = {}
  ): Promise<AgentResponse> {
    const request: AgentRequest = {
      model: options.model ?? this.defaultModel,
      prompt: prompt,
      context: options.context,
      instruction: options.instruction,
      responseSchema: options.responseSchema,
      config: options.config,
      history: this.buildHistory(options.history),
      tools: options.tools,
    };

    this.historyService.addUserMessage(prompt);

    const response = await firstValueFrom(
      this.http.post<AgentResponse>(this.endpoint, request)
    );

    this.storeModelMessage(response?.content);
    return response;
  }

  clearHistory(): void {
    this.historyService.clear();
  }

  getHistory(): AgentContent[] {
    return this.historyService.getHistorySnapshot();
  }

  private buildHistory(overrideHistory?: AgentContent[]): AgentContent[] {
    return overrideHistory ?? this.historyService.getHistorySnapshot();
  }

  private storeModelMessage(content: unknown): void {
    if (content === undefined || content === null) {
      return;
    }
    const text =
      typeof content === 'string' ? content : JSON.stringify(content);
    this.historyService.addModelMessage(text);
  }
}
